import {
  Button,
  Divider,
  InputBase,
  InputAdornment,
  IconButton,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { ErrorOutline, Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { setTokens } from "../../utils/authTokens";
import { fetchAndSetOrganization, getOrgIdFromUser } from "../../utils/organizationService";
import SigninWithGoogle from "./SigninWithGoogle";
import FlowModal from "../modals/FlowModal";
import SwitchToOldUIModal from "../modals/SwitchToOldUIModal";
import posthog from "posthog-js";
import Front from "@frontapp/plugin-sdk";
import { Mail, Lock, Eye, EyeOff, AlertCircle } from "lucide-react";
import Logo from "../reusables/Logo";
import { brand } from "../../theme/brandTokens";

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get("redirect");
  const [openFlowModal, setOpenFlowModal] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [user, setUser] = useState({
    email: "",
    password: "",
    login_from_google: false
  });
  const[remember_me, setRememberMe] = useState(true)
  const [err_msg, setErrorMsg] = useState(null);
  const [loader, setLoader] = useState(false)
  const [isFrontPlugin, setIsFrontPlugin] = useState(false);
  const [frontUserEmail, setFrontUserEmail] = useState(null);
  const [showClassicModal, setShowClassicModal] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user"));
    const org = JSON.parse(localStorage.getItem("organization"));
    if (!storedUser) return;
    if (redirectTo) return;
    if (storedUser && org) {
      navigate("/dashboard");
      return;
    }
    if (storedUser && !org) {
      const orgId = getOrgIdFromUser(storedUser);
      if (orgId) {
        fetchAndSetOrganization(orgId)
          .then(() => navigate("/dashboard"))
          .catch(() => {
            console.info("[Auth] Redirecting to create-organization: user is logged in but has no organization in storage.");
            navigate("/create-organization");
          });
        return;
      }
      console.info("[Auth] Redirecting to create-organization: user is logged in but has no organization in storage.");
      navigate("/create-organization");
    }
  }, [redirectTo, navigate]);

  useEffect(() => {
    // Check if we're running as a Front plugin and subscribe to updates
    const checkFrontPlugin = async () => {
      try {
        // Subscribe to context updates
        const subscribe = Front.contextUpdates.subscribe((context) => {
          //('Front context received:', context);
          
          if (context && context.teammate && context.teammate.email) {
            setIsFrontPlugin(true);
            setFrontUserEmail(context.teammate.email);
            //console.log('Front email set to:', context.teammate.email);
          } else {
            setIsFrontPlugin(false);
            setFrontUserEmail(null);
            //console.log('No Front user found in context');
          }
        });

        // Cleanup subscription on unmount
        return () => {
          subscribe.unsubscribe();
        };
      } catch (error) {
        //console.log('Not running as Front plugin:', error);
        setIsFrontPlugin(false);
        setFrontUserEmail(null);
      }
    };
    
    checkFrontPlugin();
  }, []);

  const login = async () => {
    setErrorMsg(null);
    setLoader(true);

    // Check Front email if running as plugin
    if (isFrontPlugin && frontUserEmail) {
      //console.log('Comparing emails:', {
      //  frontEmail: frontUserEmail,
      //  loginEmail: user.email
      //});
      
      if (user.email.toLowerCase() !== frontUserEmail.toLowerCase()) {
        setErrorMsg(`Your login email must match your Front email address (${frontUserEmail})`);
        setLoader(false);
        return;
      }
    }

    try {
        console.info("[Auth] Login:", user.email, "(email/password)");
        let user_data = await axios.post(
          process.env.REACT_APP_BACKEND_URL + "get-user",
          user
        )

        const org_id = getOrgIdFromUser(user_data.data.user);

      localStorage.setItem("user", JSON.stringify(user_data.data.user));
      if (user_data.data.access_token) {
        setTokens({
          access_token: user_data.data.access_token,
          refresh_token: user_data.data.refresh_token,
          expires_in: user_data.data.expires_in,
        });
      }

      if (redirectTo && redirectTo.startsWith("/") && !redirectTo.startsWith("//")) {
        if (org_id) {
          await fetchAndSetOrganization(org_id);
        }
        navigate(redirectTo);
        setLoader(false);
        return;
      }
      if (org_id) {
        await fetchAndSetOrganization(org_id);
        navigate("/dashboard/");
      } else {
        console.info("[Auth] Redirecting to create-organization: login succeeded but user has no organizations.");
        navigate("/create-organization");
      }

      setLoader(false);
    } catch (error) {
      // Log for production diagnostics when login fails
      console.error("Login: get-user failed", error.response?.data || error.message || error);
      if (error.response && error.response.data) {
        setErrorMsg(error.response.data.message)
      }
      setLoader(false)
    }
  };

  const handleTogglePassword = () => {
    setShowPassword((prev) => !prev);
  };

  const handleInputChange = (field, value) => {
    setUser(prev => ({ ...prev, [field]: value }));
    if (err_msg) setErrorMsg(null);
  };

  return (
    <div>
      <FlowModal open={openFlowModal} setOpen={setOpenFlowModal}/>
      <SwitchToOldUIModal open={showClassicModal} onClose={() => setShowClassicModal(false)} />
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <div className="flex justify-center">
            <Logo size="md" />
          </div>
          
          {/* Heading */}
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Sign in to your account
          </h2>
          
          {/* Description */}
          <p className="mt-2 text-center text-sm text-gray-600">
            Or{' '}
            <Link to="/register" className="font-medium text-brand-primary hover:text-brand-hover">
              start your 14-day free trial
            </Link>
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-xl sm:px-10">
            {/* Google Sign In Button - avoid mb-6 so index.css doesn't force display:block on the button (causes text to wrap) */}
            <div style={{ marginBottom: "1.5rem" }}>
              <SigninWithGoogle 
                page='login' 
                user={user} 
                setErrormsg={setErrorMsg} 
                navigate={navigate} 
                loader={loader} 
                setLoader={setLoader}
                redirectTo={redirectTo}
              />
            </div>

            <div className="flex items-center gap-3" style={{ marginBottom: "1.5rem" }}>
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-sm text-gray-500">Or login with</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            {/* Login Form */}
            <form onSubmit={(e) => { e.preventDefault(); login(); }} className="space-y-6">
              {/* Error Message */}
              {err_msg && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">
                        Sign in failed
                      </h3>
                      <div className="mt-2 text-sm text-red-700">
                        <p>{err_msg}</p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Email Field */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email address
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <InputBase
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={user.email}
                    onChange={(e) => handleInputChange('email', e.target.value.toLowerCase())}
                    disabled={loader}
                    placeholder="Enter your email"
                    sx={{
                      bgcolor: "white",
                      border: "1px solid #D1D5DB",
                      width: "100%",
                      pl: "40px",
                      pr: "12px",
                      py: "8px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      "&:focus": {
                        outline: "none",
                        ring: "2px",
                        ringColor: brand.primary,
                        borderColor: brand.primary,
                      },
                      "&:disabled": {
                        bgcolor: "#F9FAFB",
                        color: "#6B7280",
                      },
                    }}
                  />
                </div>
              </div>

              {/* Password Field */}
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Password
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <InputBase
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    required
                    value={user.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    disabled={loader}
                    placeholder="Enter your password"
                    sx={{
                      bgcolor: "white",
                      border: "1px solid #D1D5DB",
                      width: "100%",
                      pl: "40px",
                      pr: "40px",
                      py: "8px",
                      borderRadius: "6px",
                      fontSize: "14px",
                      "&:focus": {
                        outline: "none",
                        ring: "2px",
                        ringColor: brand.primary,
                        borderColor: brand.primary,
                      },
                      "&:disabled": {
                        bgcolor: "#F9FAFB",
                        color: "#6B7280",
                      },
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleTogglePassword}
                    disabled={loader}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                    ) : (
                      <Eye className="h-5 w-5 text-gray-400 hover:text-gray-500" />
                    )}
                  </button>
                </div>
              </div>

              {/* Remember me and Forgot password */}
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <Checkbox
                    color="primary"
                    checked={!remember_me}
                    onChange={(e) => {
                      if(e.target.checked){
                        setRememberMe(false)
                      } else {
                        setRememberMe(true)
                      }
                    }}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-brand-primary hover:text-brand-hover"
                  >
                    Forgot your password?
                  </Link>
                </div>
              </div>

              {/* Submit Button */}
              <div>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  color="primary"
                  disabled={loader || !user.email || !user.password}
                  sx={{
                    py: "10px",
                    px: "16px",
                    borderRadius: "6px",
                    fontSize: "14px",
                    fontWeight: 500,
                    "&:disabled": {
                      opacity: 0.5,
                      cursor: "not-allowed",
                    },
                  }}
                >
                  {loader ? (
                    <CircularProgress size={"1rem"} sx={{ color: "white" }} />
                  ) : (
                    "Sign in"
                  )}
                </Button>
              </div>

              {/* Classic UI switch */}
              <div className="pt-2 text-center">
                <button
                  type="button"
                  className="text-xs text-gray-400 hover:text-gray-600 underline"
                  onClick={() => setShowClassicModal(true)}
                >
                  Having trouble? Switch to classic login
                </button>
              </div>
            </form>

            {/* Sign up link */}
            <div className="mt-6 text-center text-sm text-gray-600">
              Don't have an account yet?{" "}
              <Link
                to="/register"
                className="font-medium text-brand-primary hover:text-brand-hover"
              >
                New Account
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
